// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What a snap looks like on the bench (M13-13, 11 §3.1).
//
// **A snap you cannot see is a snap that reads as the canvas ignoring the pointer.** The 2-D
// Builder draws a dashed line along each axis that something other than the grid caught, and a
// dot on the point (`paint.ts`, `paintSnap`). The bench has no pointer and no screen: the part is
// in the author's hand, and the point it will land on is a ghost somewhere under the fingers. So
// the bench says *more*, not less — which device the part lined up with, and where that device
// is — because the author cannot read it off an inspector.
//
// The vocabulary, from `snapping.ts`'s three rules:
//
// | The rule that caught an axis | What is drawn | What the label says |
// |---|---|---|
// | `geometry` in x | a line from the ghost to the partner's anchor, and a mark on it | *in line with popBumper2* |
// | `geometry` in y | the same, across | *level with slingLeft* |
// | both, on one anchor (`onPoint`) | a mark on the anchor | *on popBumper2* |
// | `symmetry` against a device | the centre line lit, a mark on the mirror partner, a line across from it | *mirror of slingLeft* |
// | `symmetry` with nothing (the centre line) | the centre line lit | *on the centre line* |
// | `grid` | nothing | nothing |
//
// **The grid is silent** — every carry ends on the grid, so a guide saying so would be
// permanent decoration and a click saying so would be a click on every frame. The one thing the
// grid gets is the reticle: a small ring at the *raw* point, where the fingers actually are, so
// the author can see the part being pulled off the finger by whatever rule caught it.
//
// **A rule change is one event.** `key` names the rules and partners in play; the runtime clicks
// (hands) or ticks (a controller) and flashes the marks when it changes to something that is not
// grid-only, and says nothing when a rule lets go. Pure: no Three, no Unity, no clock. The native
// client's `SnapView.cs` is this file, and the two are held together case for case.

import {type EditorPart as MachineDevice,deviceAnchors} from '../../builder/parts'
import { BENCH_EN, benchText, type BenchStrings } from './benchStrings'
import {   type Point2 } from '../../../core/ts/machine'
import type { PlayfieldSize } from '../../builder/viewport'
import type { AxisSnap, SnapResult } from '../../builder/snapping'

/** A guide line, in playfield metres. */
export interface GuideLine {
  /** `partner`: to the anchor that supplied an alignment; `centre`: the playfield's centre line; `mirror`: across from the mirror partner. */
  readonly kind: 'partner' | 'centre' | 'mirror'
  readonly from: Point2
  readonly to: Point2
}

/** A mark on a device's anchor: the one the part lined up with, or the one it mirrors. */
export interface GuideMark {
  readonly kind: 'partner' | 'mirror'
  readonly deviceId: string
  readonly at: Point2
}

export interface SnapView {
  /** Where the part will land: the snapped point, where the label hangs. */
  readonly at: Point2
  readonly lines: readonly GuideLine[]
  readonly marks: readonly GuideMark[]
  /** Where the fingers are, when a rule moved the part off them; nothing when it landed where they are. */
  readonly raw: Point2 | undefined
  /** The rule in the author's words, or nothing for the grid. */
  readonly label: string | undefined
  /** The rules and partners in play. A change is what the click and the flash fire on. */
  readonly key: string
  /** True when something other than the grid caught the part: the state a change *into* is announced. */
  readonly caught: boolean
}

/** Closer than this, in metres, is the same point: a tenth of the grid. */
const SAME = 0.0001

function same(a: number, b: number): boolean {
  return Math.abs(a - b) < SAME
}

/**
 * The anchor of `deviceId` that supplied a value in one axis: the one at that value, nearest the
 * point in the other axis. `mirrored` reads the anchor's x reflected across the centre line.
 */
function partnerAnchor(
  devices: readonly MachineDevice[],
  deviceId: string,
  axis: 0 | 1,
  value: number,
  near: Point2,
  mirrored = false,
): Point2 | undefined {
  const device = devices.find((d) => d.id === deviceId)
  if (device === undefined) return undefined
  let best: Point2 | undefined
  let bestGap = Number.POSITIVE_INFINITY
  for (const anchor of deviceAnchors(device)) {
    const read = mirrored ? -anchor.at[0] : anchor.at[axis]
    if (!same(read, value)) continue
    // The other axis: y for an x-alignment, x for a y-alignment.
    const gap = axis === 0 ? Math.abs(anchor.at[1] - near[1]) : Math.abs(anchor.at[0] - near[0])
    if (gap < bestGap) {
      bestGap = gap
      best = anchor.at
    }
  }
  return best
}

function phrase(axis: 0 | 1, snap: AxisSnap, strings: BenchStrings): string | undefined {
  if (snap.rule === 'grid') return undefined
  if (snap.rule === 'symmetry') {
    return snap.against === undefined ? benchText(strings, 'snap.centreLine') : benchText(strings, 'snap.mirrorOf', { name: snap.against })
  }
  if (snap.against === undefined) return undefined
  return benchText(strings, axis === 0 ? 'snap.inLineWith' : 'snap.levelWith', { name: snap.against })
}

/**
 * What to draw and say for this snap, or nothing while there is no snap.
 *
 * `devices` is what the snap was made against — every device but the one in hand — so a partner
 * named by the snap is always found in it. `strings` is the language the label is in (M13-26).
 */
export function snapViewOf(
  snap: SnapResult | undefined,
  raw: Point2 | undefined,
  devices: readonly MachineDevice[],
  playfield: PlayfieldSize,
  strings: BenchStrings = BENCH_EN,
): SnapView | undefined {
  if (snap === undefined) return undefined
  const lines: GuideLine[] = []
  const marks: GuideMark[] = []
  const at = snap.at
  let label: string | undefined

  if (snap.onPoint && snap.x.against !== undefined) {
    const id = snap.x.against
    const anchor = partnerAnchor(devices, id, 0, at[0], at) ?? at
    marks.push({ kind: 'partner', deviceId: id, at: anchor })
    label = benchText(strings, 'snap.on', { name: id })
  } else {
    const phrases: string[] = []
    if (snap.x.rule === 'geometry' && snap.x.against !== undefined) {
      const anchor = partnerAnchor(devices, snap.x.against, 0, at[0], at)
      if (anchor !== undefined) {
        lines.push({ kind: 'partner', from: at, to: [at[0], anchor[1]] })
        marks.push({ kind: 'partner', deviceId: snap.x.against, at: anchor })
      }
    } else if (snap.x.rule === 'symmetry') {
      lines.push({ kind: 'centre', from: [0, -playfield.length / 2], to: [0, playfield.length / 2] })
      if (snap.x.against !== undefined) {
        const anchor = partnerAnchor(devices, snap.x.against, 0, at[0], at, true)
        if (anchor !== undefined) {
          lines.push({ kind: 'mirror', from: anchor, to: [at[0], anchor[1]] })
          marks.push({ kind: 'mirror', deviceId: snap.x.against, at: anchor })
        }
      }
    }
    if (snap.y.rule === 'geometry' && snap.y.against !== undefined) {
      const anchor = partnerAnchor(devices, snap.y.against, 1, at[1], at)
      if (anchor !== undefined) {
        lines.push({ kind: 'partner', from: at, to: [anchor[0], at[1]] })
        marks.push({ kind: 'partner', deviceId: snap.y.against, at: anchor })
      }
    }
    const x = phrase(0, snap.x, strings)
    const y = phrase(1, snap.y, strings)
    if (x !== undefined) phrases.push(x)
    if (y !== undefined) phrases.push(y)
    label = phrases.length === 0 ? undefined : phrases.join(strings['snap.and'])
  }

  const caught = snap.x.rule !== 'grid' || snap.y.rule !== 'grid'
  const key = `${snap.x.rule}:${snap.x.against ?? ''}|${snap.y.rule}:${snap.y.against ?? ''}|${snap.onPoint ? 'point' : ''}`
  const moved = raw !== undefined && !(same(raw[0], at[0]) && same(raw[1], at[1]))
  return { at, lines, marks, raw: moved ? raw : undefined, label, key, caught }
}

/**
 * Whether going from one view to the next is a rule change worth a click and a flash.
 *
 * Into something caught, from nothing or from a different rule: yes. Into grid-only, or out to
 * no snap at all: silent — the guides going dark is the whole of that news.
 */
export function ruleChanged(before: SnapView | undefined, after: SnapView | undefined): boolean {
  if (after === undefined || !after.caught) return false
  return before === undefined || before.key !== after.key
}
